import { prefersReducedMotion } from '@theme/utilities';

// GSAP's start: "top 80%" - fire once the top edge has passed 80% down the viewport.
const REVEAL_MARGIN = '0px 0px -20% 0px';

// Past this, per-character stagger drags for seconds, so the whole block fades as one instead.
const MAX_CHARS = 200;

const segmenter =
  typeof Intl !== 'undefined' && Intl.Segmenter ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;

/**
 * Splits a word into graphemes, so emoji and combining marks stay intact.
 *
 * @param {string} word
 * @returns {string[]}
 */
function toGraphemes(word) {
  if (!segmenter) return Array.from(word);
  return Array.from(segmenter.segment(word), (entry) => entry.segment);
}

/**
 * Reveals its text on scroll, optionally one character at a time.
 *
 * The characters are wrapped at runtime rather than in Liquid so rich text keeps its markup, and
 * they sit inside nowrap word spans so a resize or a late font swap never needs a re-split.
 *
 * Nothing here animates: the element only sets data attributes, and CSS keyframes do the motion.
 */
class WomText extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    // Reduced motion never splits - the text is simply revealed as authored.
    if (prefersReducedMotion()) {
      this.dataset.womTextReady = '';
      return;
    }

    this.dataset.womTextMode = this.#split() ? 'chars' : 'block';

    // Lifts the pre-split hide in CSS. Must come after the split so nothing paints mid-wrap.
    this.dataset.womTextReady = '';

    // This host is display: contents, so it has no box of its own to intersect - watch the text instead.
    const target = this.firstElementChild ?? this;

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          // Already scrolled past on load - show it outright rather than replaying on the way back up.
          if (entry.boundingClientRect.top < 0) this.dataset.womTextMode = 'none';

          this.dataset.womTextState = 'shown';
          observer.unobserve(entry.target);
          this.#observer?.disconnect();
          this.#observer = null;
        }
      },
      { rootMargin: REVEAL_MARGIN }
    );

    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Wraps every character in its own span, carrying a running index for the stagger delay.
   *
   * @returns {boolean} False when the text is too long to stagger, leaving the markup untouched.
   */
  #split() {
    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);

    /** @type {Text[]} */
    const textNodes = [];
    let total = 0;

    // Collected up front, because replacing a node mid-walk invalidates the walker.
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = /** @type {Text} */ (node);
      const tag = text.parentElement?.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE') continue;

      const trimmed = text.data.replace(/\s+/g, '');
      if (!trimmed) continue;

      textNodes.push(text);
      total += trimmed.length;
    }

    if (!textNodes.length || total > MAX_CHARS) return false;

    const label = this.textContent ?? '';
    let index = 0;

    for (const text of textNodes) {
      const fragment = document.createDocumentFragment();

      // Whitespace runs go back as plain text, so the browser still breaks lines where it would have.
      for (const part of text.data.split(/(\s+)/)) {
        if (!part) continue;

        if (!part.trim()) {
          fragment.append(part);
          continue;
        }

        const word = document.createElement('span');
        word.className = 'wom-text__word';

        for (const grapheme of toGraphemes(part)) {
          const char = document.createElement('span');
          char.className = 'wom-text__char';
          char.style.setProperty('--wom-char-index', String(index));
          char.textContent = grapheme;
          word.append(char);
          index += 1;
        }

        fragment.append(word);
      }

      text.replaceWith(fragment);
    }

    // A span per character gets spelled out, so an unselectable plain-text twin carries the reading instead.
    for (const child of this.children) child.setAttribute('aria-hidden', 'true');

    const readable = document.createElement('span');
    readable.className = 'visually-hidden wom-text__label';
    readable.textContent = label;
    this.append(readable);

    return true;
  }
}

if (!customElements.get('wom-text')) {
  customElements.define('wom-text', WomText);
}
